import { useMemo } from 'react'
import { FileClock, PenTool, ShieldCheck, UserCheck, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { WireArticle } from '../../api/types'
import { useEditorialManagers, useLayoutAndDesignMembers, usePostProductionMembers, useQualityControlMembers } from '../../api/hooks/useAdmin'

type TeamRoleKey = 'qc_review' | 'layout_review' | 'editorial_approval' | 'post_pub_review'

type TeamRoleMeta = {
  key: TeamRoleKey
  label: string
  icon: LucideIcon
  membersCount: number
  assignedCount: number
  unassignedCount: number
  blockedCount: number
  sampleNames: string[]
}

interface TeamLoadWidgetProps {
  articles: WireArticle[]
  embedded?: boolean
}

function roleLabel(role: TeamRoleKey): string {
  switch (role) {
    case 'qc_review':
      return 'QC Review'
    case 'layout_review':
      return 'Layout'
    case 'editorial_approval':
      return 'Editorial'
    case 'post_pub_review':
      return 'Post-Pub'
  }
}

function roleIcon(role: TeamRoleKey): LucideIcon {
  switch (role) {
    case 'qc_review':
      return ShieldCheck
    case 'layout_review':
      return PenTool
    case 'editorial_approval':
      return UserCheck
    case 'post_pub_review':
      return FileClock
  }
}

function roleField(role: TeamRoleKey): keyof NonNullable<WireArticle['workflow']> {
  switch (role) {
    case 'qc_review':
      return 'qcAssignedTo'
    case 'layout_review':
      return 'layoutAssignedTo'
    case 'editorial_approval':
      return 'editorialAssignedTo'
    case 'post_pub_review':
      return 'postPubAssignedTo'
  }
}

function countRoleLoad(articles: WireArticle[], role: TeamRoleKey) {
  const field = roleField(role)

  const assignedCount = articles.filter((article) => article.workflow?.stage === role && Boolean(article.workflow?.[field])).length
  const unassignedCount = articles.filter((article) => article.workflow?.stage === role && !article.workflow?.[field]).length
  const blockedCount = articles.filter((article) => article.workflow?.stage === role && Boolean(article.workflow?.activeBlock)).length

  return { assignedCount, unassignedCount, blockedCount }
}

function TeamRoleRow({ role, loading }: { role: TeamRoleMeta; loading: boolean }) {
  const Icon = role.icon

  return (
    <div className="flex items-start justify-between gap-3 rounded-xl bg-canvas px-4 py-3 transition-colors hover:bg-fill/70">
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-fill text-fg-secondary">
          <Icon size={14} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-sm font-semibold text-fg">{role.label}</p>
            <span className="rounded-full bg-fill px-2 py-0.5 text-[10px] font-semibold text-fg-secondary">
              {role.membersCount} members
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-fg-secondary">
            {loading ? 'Loading...' : `${role.assignedCount} assigned · ${role.unassignedCount} unassigned · ${role.blockedCount} blocked`}
          </p>
          {!loading && role.sampleNames.length > 0 && (
            <p className="mt-1 truncate text-[11px] text-fg-secondary">
              {role.sampleNames.join(' · ')}
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-right">
        <span className="rounded-full bg-fill px-2 py-0.5 text-[10px] font-semibold text-fg-secondary">
          {role.assignedCount + role.unassignedCount}
        </span>
        <span className="text-[10px] font-medium text-fg-secondary">open items</span>
      </div>
    </div>
  )
}

export function TeamLoadWidget({ articles, embedded = false }: TeamLoadWidgetProps) {
  const { data: qcMembers = [], isPending: qcLoading } = useQualityControlMembers()
  const { data: layoutMembers = [], isPending: layoutLoading } = useLayoutAndDesignMembers()
  const { data: editorialMembers = [], isPending: editorialLoading } = useEditorialManagers()
  const { data: postPubMembers = [], isPending: postPubLoading } = usePostProductionMembers()

  const roles = useMemo<TeamRoleMeta[]>(() => {
    const roleData: Array<{ key: TeamRoleKey; names: string[]; loading: boolean }> = [
      { key: 'qc_review', names: qcMembers.map((member) => member.name), loading: qcLoading },
      { key: 'layout_review', names: layoutMembers.map((member) => member.name), loading: layoutLoading },
      { key: 'editorial_approval', names: editorialMembers.map((member) => member.name), loading: editorialLoading },
      { key: 'post_pub_review', names: postPubMembers.map((member) => member.name), loading: postPubLoading },
    ]

    return roleData.map(({ key, names, loading }) => {
      const counts = countRoleLoad(articles, key)
      return {
        key,
        label: roleLabel(key),
        icon: roleIcon(key),
        membersCount: names.length,
        assignedCount: counts.assignedCount,
        unassignedCount: counts.unassignedCount,
        blockedCount: counts.blockedCount,
        sampleNames: loading ? [] : names.slice(0, 2),
      }
    })
  }, [articles, qcMembers, layoutMembers, editorialMembers, postPubMembers, qcLoading, layoutLoading, editorialLoading, postPubLoading])

  const loading = qcLoading || layoutLoading || editorialLoading || postPubLoading
  const totalOpen = roles.reduce((sum, role) => sum + role.assignedCount + role.unassignedCount, 0)
  const totalBlocked = roles.reduce((sum, role) => sum + role.blockedCount, 0)

  const body = (
    <div className="space-y-2 px-4 py-4">
      <div className="flex items-center justify-between gap-3 rounded-xl bg-fill/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <Users size={14} aria-hidden="true" className="text-fg-secondary" />
          <p className="text-sm font-semibold text-fg">Team Load</p>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-semibold text-fg-secondary">
          <span>{totalOpen} open</span>
          <span>·</span>
          <span>{totalBlocked} blocked</span>
        </div>
      </div>

      <div className="space-y-2">
        {roles.map((role) => (
          <TeamRoleRow key={role.key} role={role} loading={loading} />
        ))}
      </div>
    </div>
  )

  if (embedded) {
    return <div>{body}</div>
  }

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Users size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
          Team Load
        </span>
      </div>
      {body}
    </section>
  )
}
